import { useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  CheckCircle2,
  CircleArrowLeft,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { type CreateSessionParams } from '~entities/session/api/create-session';
import {
  ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY,
  getSessionRolloverErrorMessage,
  isMissingSessionRolloverJobError,
  type RolloverDateMode,
  type RolloverEnrollmentDisposition,
  type RolloverMode,
  type SessionRolloverFeesInput,
  type SessionRolloverJob,
  useCancelSessionRollover,
  useCreateV2RolloverSession,
  useDeleteSessionRollover,
  useRetrySessionRollover,
  useSessionRolloverJob,
  useSessionRolloverPreview,
  useStartSessionRollover,
  useV2RolloverSession,
} from '~entities/session/api/session-rollover';

import { type SessionOption } from '~features/shared/session-selector';

import { Stepper } from '~shared/components/stepper';
import { InputDatePicker } from '~shared/components/input-date-picker';
import { INPUT_DATE_PICKER_ICON_POSITION } from '~shared/components/input-date-picker.types';
import { Alert, AlertDescription, AlertTitle } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { Progress } from '~shared/shadcn/ui/progress';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';
import { Switch } from '~shared/shadcn/ui/switch';
import { formatDateIgnoreTimezone } from '~shared/utils/date-time';

import { SessionForm } from './session-form';
import { SessionRolloverSelector } from './session-rollover-selector';

const CLASSES_PER_PAGE = 25;

const fullRolloverSteps = [
  { id: 'session', label: 'Pick sessions' },
  { id: 'migration', label: 'Classes and students' },
  { id: 'invoicing', label: 'Fees' },
];

const classesOnlySteps = [
  { id: 'session', label: 'Pick sessions' },
  { id: 'migration', label: 'Copy classes' },
];

const noFeeSelection: SessionRolloverFeesInput = {
  sessionRegistration: false,
  classRegistration: false,
  costume: false,
};

const terminalStatuses = new Set([
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
  'deleted',
]);

const shouldKeepStoredRolloverJob = (job: SessionRolloverJob) =>
  job.status === 'queued' || job.status === 'processing' || job.canRetry;

export interface SessionRolloverPageProps {
  initialSourceSessionId?: string;
}

export function SessionRolloverPage({
  initialSourceSessionId = '',
}: SessionRolloverPageProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [source, setSource] = useState<SessionOption | null>(null);
  const [destination, setDestination] = useState<SessionOption | null>(null);
  const [destinationMode, setDestinationMode] = useState<'existing' | 'create'>(
    'existing',
  );
  const [destinationCreatedInWizard, setDestinationCreatedInWizard] =
    useState<boolean>(false);
  const [dateMode, setDateMode] = useState<RolloverDateMode>('shift');
  const [classPage, setClassPage] = useState(1);
  const [disposition, setDisposition] =
    useState<RolloverEnrollmentDisposition>('history');
  const [rolloverMode, setRolloverMode] = useState<RolloverMode>('copy');
  const [resetExistingSubscriptions, setResetExistingSubscriptions] =
    useState(false);
  const [selectedClassIds, setSelectedClassIds] = useState<Set<string>>(
    new Set(),
  );
  const [classDateOverrides, setClassDateOverrides] = useState<
    Record<string, { startDate: string; endDate: string }>
  >({});
  const [fees, setFees] = useState<SessionRolloverFeesInput>({
    sessionRegistration: false,
    classRegistration: false,
    costume: false,
    chargeSessionRegistrationForContinuing: true,
    chargeClassRegistrationForContinuing: true,
  });
  const [costumeAmountInput, setCostumeAmountInput] = useState('');
  const [futureCostumeFeeInput, setFutureCostumeFeeInput] = useState('');
  const [futureClassRegFeeInput, setFutureClassRegFeeInput] = useState('');
  const [futureSessionFeeInput, setFutureSessionFeeInput] = useState('');
  const [jobId, setJobId] = useState(
    () => sessionStorage.getItem(ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY) ?? '',
  );
  const selectionSessionKeyRef = useRef('');
  const notifiedJobStatusRef = useRef('');

  const { data: initialSource } = useV2RolloverSession(initialSourceSessionId);
  const { data: sourceSession } = useV2RolloverSession(source?.value ?? '', {
    enabled: Boolean(source?.value),
  });
  const { data: destinationSession } = useV2RolloverSession(
    destination?.value ?? '',
    { enabled: Boolean(destination?.value) },
  );
  const createMutation = useCreateV2RolloverSession();
  const startMutation = useStartSessionRollover();
  const {
    data: preview,
    isFetching: previewLoading,
    error: previewError,
  } = useSessionRolloverPreview({
    sourceSessionId: source?.value ?? '',
    destinationSessionId: destination?.value ?? '',
    dateMode,
    page: classPage,
    limit: CLASSES_PER_PAGE,
    enabled: !jobId,
  });
  const { data: job, error: jobError } = useSessionRolloverJob(jobId, {
    enabled: Boolean(jobId),
  });

  useEffect(() => {
    if (initialSource && !source) {
      setSource({ label: initialSource.name, value: initialSource._id });
    }
  }, [initialSource, source]);

  useEffect(() => {
    setClassPage(1);
    setSelectedClassIds(new Set());
    setClassDateOverrides({});
    selectionSessionKeyRef.current = '';
  }, [source?.value, destination?.value]);

  useEffect(() => {
    if (!preview || !source?.value || !destination?.value) return;
    const sessionKey = `${source.value}-${destination.value}`;
    if (selectionSessionKeyRef.current === sessionKey) return;
    setSelectedClassIds(new Set(preview.allClassIds));
    selectionSessionKeyRef.current = sessionKey;
  }, [destination?.value, preview, source?.value]);

  useEffect(() => {
    if (!jobId) return;
    if (isMissingSessionRolloverJobError(jobError)) {
      sessionStorage.removeItem(ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY);
      setJobId('');
      return;
    }
    if (!job) return;
    if (shouldKeepStoredRolloverJob(job)) {
      sessionStorage.setItem(
        ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY,
        job.jobId,
      );
    } else if (terminalStatuses.has(job.status)) {
      sessionStorage.removeItem(ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY);
    }
    if (!terminalStatuses.has(job.status)) return;
    const notifyKey = `${job.jobId}:${job.status}`;
    if (notifiedJobStatusRef.current === notifyKey) return;
    notifiedJobStatusRef.current = notifyKey;
    if (job.status === 'completed') {
      toast.success('Session rollover completed.');
    } else if (job.status === 'completed_with_errors') {
      toast.warning('Session rollover completed with some errors.');
    } else if (job.status === 'failed') {
      toast.error(job.errorMessage ?? 'Session rollover failed.');
    }
  }, [job, jobError, jobId]);

  const createDestination = async (values: CreateSessionParams) => {
    const created = await createMutation.mutateAsync(values);
    setDestination({ label: created.name, value: created._id });
    setDestinationCreatedInWizard(true);
    toast.success(`${created.name} created. Continue when ready.`);
  };

  const sessionGapSummary = (() => {
    if (!sourceSession || !destinationSession) return '';
    const newStart = formatDateIgnoreTimezone(destinationSession.startDate);
    if (preview?.deltaDays === undefined) {
      return `${destinationSession.name} starts on ${newStart}.`;
    }
    if (preview.deltaDays === 0) {
      return `${destinationSession.name} starts on the same date as ${sourceSession.name} (${newStart}), so your class dates stay exactly as they are.`;
    }
    const gap = Math.abs(preview.deltaDays);
    const dayLabel = gap === 1 ? 'day' : 'days';
    const direction = preview.deltaDays > 0 ? 'later' : 'earlier';
    return `${destinationSession.name} starts on ${newStart}, which is ${gap} ${dayLabel} ${direction} than ${sourceSession.name}. Each class you copy moves by the same ${gap} ${dayLabel}, so it keeps its usual day of the week and time. You can change any class date on the next step.`;
  })();

  const classesOnlyCopy =
    rolloverMode === 'copy' && disposition === 'keep';
  const steps = classesOnlyCopy ? classesOnlySteps : fullRolloverSteps;

  const selectedClassDatesValid = Array.from(selectedClassIds).every(
    (classId) => {
      const dates = classDateOverrides[classId];
      return (
        !dates ||
        new Date(dates.endDate).getTime() >= new Date(dates.startDate).getTime()
      );
    },
  );
  const canContinue =
    currentStep === 0
      ? Boolean(
          source?.value &&
            destination?.value &&
            source.value !== destination.value &&
            !preview?.blockingRollover,
        )
      : currentStep === 1
        ? selectedClassIds.size > 0 &&
          selectedClassDatesValid &&
          !preview?.blockingRollover
        : !preview?.blockingRollover;

  const startRollover = async () => {
    if (!source || !destination) return;
    const costumeAmount =
      fees.costume && costumeAmountInput.trim()
        ? Number(costumeAmountInput)
        : undefined;
    const futureCostumeFee = futureCostumeFeeInput.trim()
      ? Number(futureCostumeFeeInput)
      : undefined;
    const futureClassRegFee = futureClassRegFeeInput.trim()
      ? Number(futureClassRegFeeInput)
      : undefined;
    const futureSessionFee = futureSessionFeeInput.trim()
      ? Number(futureSessionFeeInput)
      : undefined;
    const futureFees =
      !classesOnlyCopy &&
      (futureCostumeFee !== undefined ||
        futureClassRegFee !== undefined ||
        futureSessionFee !== undefined)
        ? {
            ...(futureCostumeFee !== undefined
              ? { costumeFee: futureCostumeFee }
              : {}),
            ...(futureClassRegFee !== undefined
              ? { classRegistrationFeeAmount: futureClassRegFee }
              : {}),
            ...(futureSessionFee !== undefined
              ? { sessionRegistrationPerStudent: { 1: futureSessionFee } }
              : {}),
          }
        : undefined;
    try {
      const createdJob = await startMutation.mutateAsync({
        sourceSessionId: source.value,
        destinationSessionId: destination.value,
        destinationSessionCreatedInWizard: Boolean(destinationCreatedInWizard),
        classIds: Array.from(selectedClassIds),
        dateMode,
        enrollmentDisposition:
          rolloverMode === 'move' ? 'history' : disposition,
        mode: rolloverMode,
        billing:
          rolloverMode === 'move'
            ? { resetExistingSubscriptions }
            : undefined,
        classDateOverrides: Array.from(selectedClassIds)
          .map((classId) => {
            const dates = classDateOverrides[classId];
            return dates ? { classId, ...dates } : null;
          })
          .filter(
            (
              item,
            ): item is {
              classId: string;
              startDate: string;
              endDate: string;
            } => Boolean(item),
          ),
        fees: classesOnlyCopy
          ? noFeeSelection
          : {
              ...fees,
              ...(fees.costume &&
              costumeAmount !== undefined &&
              !Number.isNaN(costumeAmount)
                ? { costumeAmount }
                : {}),
              ...(futureFees ? { futureFees } : {}),
            },
      });
      sessionStorage.setItem(
        ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY,
        createdJob.jobId,
      );
      setJobId(createdJob.jobId);
    } catch (error) {
      toast.error(
        getSessionRolloverErrorMessage(
          error,
          'Could not start the session rollover.',
        ),
      );
    }
  };

  if (jobId) {
    return (
      <SessionRolloverProgress
        job={job}
        jobId={jobId}
        onClose={() => {
          if (job && !shouldKeepStoredRolloverJob(job)) {
            sessionStorage.removeItem(ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY);
          }
          void navigate({ to: '/sessions' });
        }}
      />
    );
  }

  return (
    <div className='mx-auto w-full max-w-6xl'>
      <div className='mb-6 flex items-center gap-2'>
        <Button
          variant='ghost'
          className='flex items-center p-2 [&_svg]:size-6'
          title='Back to sessions'
          onClick={() => {
            void navigate({ to: '/sessions' });
          }}
        >
          <CircleArrowLeft className='size-6' />
        </Button>
        <div>
          <h1 className='text-2xl font-semibold'>Session Rollover Manager</h1>
          <p className='text-sm text-muted-foreground'>
            Move your classes and their students into your next session. No
            cards are charged here.
          </p>
        </div>
      </div>
      <Stepper
        steps={steps}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        canGoNext={canContinue}
        isSubmitting={startMutation.isPending}
        onBack={() => {
          setCurrentStep((step) => Math.max(0, step - 1));
        }}
        onNext={() => {
          setCurrentStep((step) => Math.min(steps.length - 1, step + 1));
        }}
        onFinish={() => void startRollover()}
        finishLabel={
          classesOnlyCopy ? 'Copy classes' : 'Start rollover'
        }
      >
        {currentStep === 0 && (
          <div className='grid gap-6'>
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>
                  Which session are you moving students out of?
                </CardTitle>
                <p className='text-sm text-muted-foreground'>
                  This is the session that is ending. Its classes and students
                  are the ones you will copy.
                </p>
              </CardHeader>
              <CardContent>
                <SessionRolloverSelector
                  session={source}
                  onChange={setSource}
                  includeArchivedSessions
                />
              </CardContent>
            </Card>

            {!selectedClassDatesValid && (
              <Alert variant='destructive'>
                <AlertCircle className='size-4' />
                <AlertDescription>
                  Every selected class must end on or after its start date.
                </AlertDescription>
              </Alert>
            )}

            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>
                  Which session are they moving into?
                </CardTitle>
                <p className='text-sm text-muted-foreground'>
                  Pick a session you already created, or create the new one
                  right here.
                </p>
              </CardHeader>
              <CardContent className='grid gap-5'>
                <RadioGroup
                  value={destinationMode}
                  onValueChange={(value) => {
                    setDestinationMode(value as 'existing' | 'create');
                    setDestination(null);
                    setDestinationCreatedInWizard(false);
                  }}
                  className='flex gap-6'
                >
                  <Label className='flex items-center gap-2'>
                    <RadioGroupItem value='existing' />
                    Choose a session I already created
                  </Label>
                  <Label className='flex items-center gap-2'>
                    <RadioGroupItem value='create' />
                    Create a new session now
                  </Label>
                </RadioGroup>

                {destinationMode === 'existing' ? (
                  <SessionRolloverSelector
                    session={destination}
                    onChange={setDestination}
                  />
                ) : destinationCreatedInWizard && destination ? (
                  <Alert>
                    <CheckCircle2 className='size-4' />
                    <AlertTitle>New session created</AlertTitle>
                    <AlertDescription>
                      {destination.label} is ready. Continue to choose the
                      classes you want to copy into it.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <SessionForm
                    handleSubmit={createDestination}
                    isPending={createMutation.isPending}
                    buttonContent='Create destination session'
                    defaultValues={
                      sourceSession
                        ? {
                            sessionName: '',
                            startDate: '',
                            endDate: '',
                            isRegistrationFeeRequired: Boolean(
                              sourceSession.isRegistrationFee,
                            ),
                            registrationFee:
                              sourceSession.registrationFeeAmount ?? 0,
                            registrationFeePerStudent:
                              sourceSession.registrationFeePerStudent,
                            visibleInParentPortal: false,
                            chargeLastMonthAtCheckout:
                              sourceSession.chargeLastMonthAtCheckout,
                            chargeSessionFeesAtEnrollment:
                              sourceSession.chargeSessionFeesAtEnrollment,
                          }
                        : undefined
                    }
                  />
                )}
              </CardContent>
            </Card>

            {source?.value === destination?.value && (
              <Alert variant='destructive'>
                <AlertCircle className='size-4' />
                <AlertDescription>
                  Pick two different sessions. You cannot copy a session into
                  itself.
                </AlertDescription>
              </Alert>
            )}

            {sourceSession && destinationSession && (
              <Alert>
                <AlertDescription>{sessionGapSummary}</AlertDescription>
              </Alert>
            )}

            {preview?.blockingRollover ? (
              <Alert variant='destructive'>
                <AlertCircle className='size-4' />
                <AlertTitle>These sessions were already rolled over</AlertTitle>
                <AlertDescription>
                  {preview.blockingRollover.status === 'completed'
                    ? 'Undo that rollover from the Sessions list before running it again.'
                    : preview.blockingRollover.status ===
                        'completed_with_errors' ||
                      preview.blockingRollover.status === 'failed'
                    ? 'Open the existing rollover and use Retry, or undo it first.'
                    : 'Another rollover for these sessions is already running. Wait for it to finish or open that job instead.'}
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}

        {currentStep === 1 && (
          <div className='grid gap-6'>
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>
                  Copy classes or move them?
                </CardTitle>
                <p className='text-sm text-muted-foreground'>
                  Copy keeps the original classes where they are and creates new
                  ones. Move repoints the same classes into the new session.
                </p>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={rolloverMode}
                  onValueChange={(value) => {
                    const nextMode = value as RolloverMode;
                    setRolloverMode(nextMode);
                    if (nextMode === 'copy') {
                      setResetExistingSubscriptions(false);
                    } else {
                      setDisposition('history');
                    }
                  }}
                  className='grid gap-3'
                >
                  <Label className='flex items-start gap-2'>
                    <RadioGroupItem value='copy' className='mt-0.5' />
                    <span>
                      Copy classes (recommended for most studios)
                      <span className='block text-xs text-muted-foreground'>
                        Creates new class records in the destination session.
                        You choose whether students move with them.
                      </span>
                    </span>
                  </Label>
                  <Label className='flex items-start gap-2'>
                    <RadioGroupItem value='move' className='mt-0.5' />
                    <span>
                      Move classes
                      <span className='block text-xs text-muted-foreground'>
                        Keeps the same class IDs and moves them into the new
                        session instead of creating copies.
                      </span>
                    </span>
                  </Label>
                </RadioGroup>
                {rolloverMode === 'move' && (
                  <div className='mt-4 flex items-center justify-between gap-6 rounded-lg border p-4'>
                    <div>
                      <Label htmlFor='reset-existing-subscriptions'>
                        Reset existing tuition schedules
                      </Label>
                      <p className='text-xs text-muted-foreground'>
                        Cancels open future invoices on the moved classes and
                        rebuilds tuition for the new term dates.
                      </p>
                    </div>
                    <Switch
                      id='reset-existing-subscriptions'
                      checked={resetExistingSubscriptions}
                      onCheckedChange={setResetExistingSubscriptions}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>
                  What start and end dates should the{' '}
                  {rolloverMode === 'move' ? 'moved' : 'copied'} classes use?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={dateMode}
                  onValueChange={(value) => {
                    setDateMode(value as RolloverDateMode);
                  }}
                  className='grid gap-3'
                >
                  <Label className='flex items-start gap-2'>
                    <RadioGroupItem value='shift' className='mt-0.5' />
                    <span>
                      Keep each class the same length, just later (recommended)
                      <span className='block text-xs text-muted-foreground'>
                        A class that ran for 8 weeks still runs for 8 weeks, on
                        the same day of the week. Registration open and close
                        dates move by the same amount.
                      </span>
                    </span>
                  </Label>
                  <Label className='flex items-start gap-2'>
                    <RadioGroupItem value='snapToSession' className='mt-0.5' />
                    <span>
                      Make every class run the full new session
                      <span className='block text-xs text-muted-foreground'>
                        Every copied class starts on the first day of the new
                        session and ends on its last day. Use this when all your
                        classes run the whole session.
                      </span>
                    </span>
                  </Label>
                </RadioGroup>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <div>
                    <CardTitle className='text-lg'>
                      Which classes should be copied?
                    </CardTitle>
                    <p className='text-sm text-muted-foreground'>
                      Every class is selected by default. Uncheck any class you
                      are not running again.
                    </p>
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setSelectedClassIds(new Set(preview?.allClassIds ?? []));
                    }}
                  >
                    Select all
                  </Button>
                </div>
              </CardHeader>
              <CardContent className='grid gap-2'>
                {previewLoading && <Loader2 className='mx-auto animate-spin' />}
                {previewError && (
                  <Alert variant='destructive'>
                    <AlertCircle className='size-4' />
                    <AlertTitle>We could not load the class list</AlertTitle>
                    <AlertDescription>
                      Please go back, re-select your sessions, and try again. If
                      this keeps happening, contact support.
                    </AlertDescription>
                  </Alert>
                )}
                {preview && !previewError && preview.classes.length === 0 && (
                  <Alert>
                    <AlertCircle className='size-4' />
                    <AlertTitle>No classes to copy</AlertTitle>
                    <AlertDescription>
                      {sourceSession?.name ?? 'That session'} has no active
                      classes. Go back and pick the session your students are
                      currently enrolled in.
                    </AlertDescription>
                  </Alert>
                )}
                {preview?.blockingRollover ? (
                  <Alert variant='destructive'>
                    <AlertCircle className='size-4' />
                    <AlertTitle>These sessions were already rolled over</AlertTitle>
                    <AlertDescription>
                      {preview.blockingRollover.status === 'completed'
                        ? 'Undo that rollover from the Sessions list before running it again.'
                        : preview.blockingRollover.status ===
                            'completed_with_errors' ||
                          preview.blockingRollover.status === 'failed'
                        ? 'Open the existing rollover and use Retry, or undo it first.'
                        : 'Another rollover for these sessions is already running. Wait for it to finish or open that job instead.'}
                    </AlertDescription>
                  </Alert>
                ) : null}
                {preview?.destinationClassCount ? (
                  <Alert>
                    <AlertCircle className='size-4' />
                    <AlertDescription>
                      {destinationSession?.name ?? 'The new session'} already
                      has {preview.destinationClassCount} class
                      {preview.destinationClassCount === 1 ? '' : 'es'}. The
                      copied classes are added alongside them, nothing is
                      overwritten.
                    </AlertDescription>
                  </Alert>
                ) : null}
                {preview?.classes.map((item) => {
                  const dateOverride =
                    classDateOverrides[item.id] ??
                    (item.proposedStartDate && item.proposedEndDate
                      ? {
                          startDate: item.proposedStartDate,
                          endDate: item.proposedEndDate,
                        }
                      : undefined);
                  return (
                    <div
                      key={item.id}
                      className='flex items-start gap-3 rounded-lg border p-3'
                    >
                      <div className='flex items-start gap-3 pt-0.5'>
                        <Checkbox
                          id={`rollover-class-${item.id}`}
                          checked={selectedClassIds.has(item.id)}
                          onCheckedChange={(checked) => {
                            setSelectedClassIds((current) => {
                              const next = new Set(current);
                              if (checked) {
                                next.add(item.id);
                              } else {
                                next.delete(item.id);
                              }
                              return next;
                            });
                          }}
                        />
                      </div>
                      <div className='flex-1'>
                        <Label
                          htmlFor={`rollover-class-${item.id}`}
                          className='font-medium'
                        >
                          {item.title}
                        </Label>
                        <p className='text-xs text-muted-foreground'>
                          {item.eligibleStudents === 0
                            ? 'No students to move'
                            : `${item.eligibleStudents} student${
                                item.eligibleStudents === 1 ? '' : 's'
                              } will be moved`}
                          {item.hourly ? ' · Hourly tuition class' : ''} · New
                          dates:{' '}
                          {item.proposedStartDate
                            ? formatDateIgnoreTimezone(item.proposedStartDate)
                            : 'no start date'}{' '}
                          to{' '}
                          {item.proposedEndDate
                            ? formatDateIgnoreTimezone(item.proposedEndDate)
                            : 'no end date'}
                        </p>
                        {item.weekdayWarning && (
                          <p className='text-xs text-amber-700'>
                            Heads up: the new start date lands on a day this
                            class does not normally meet. Adjust it below if
                            that is not what you want.
                          </p>
                        )}
                        {dateOverride && (
                          <div className='mt-2 grid grid-cols-2 gap-2'>
                            <div>
                              <Label
                                htmlFor={`rollover-class-start-${item.id}`}
                                className='mb-1 block text-xs'
                              >
                                First class date
                              </Label>
                              <InputDatePicker
                                id={`rollover-class-start-${item.id}`}
                                value={dateOverride.startDate.slice(0, 10)}
                                onChange={(nextDate) => {
                                  setClassDateOverrides((current) => ({
                                    ...current,
                                    [item.id]: {
                                      ...dateOverride,
                                      startDate: `${nextDate}T00:00:00.000Z`,
                                    },
                                  }));
                                }}
                                ariaLabel={`First class date for ${item.title}`}
                                displayFormat='MM/dd/yyyy'
                                iconPosition={
                                  INPUT_DATE_PICKER_ICON_POSITION.END
                                }
                                allowClear={false}
                                className='h-9 px-2'
                              />
                            </div>
                            <div>
                              <Label
                                htmlFor={`rollover-class-end-${item.id}`}
                                className='mb-1 block text-xs'
                              >
                                Last class date
                              </Label>
                              <InputDatePicker
                                id={`rollover-class-end-${item.id}`}
                                value={dateOverride.endDate.slice(0, 10)}
                                onChange={(nextDate) => {
                                  setClassDateOverrides((current) => ({
                                    ...current,
                                    [item.id]: {
                                      ...dateOverride,
                                      endDate: `${nextDate}T00:00:00.000Z`,
                                    },
                                  }));
                                }}
                                ariaLabel={`Last class date for ${item.title}`}
                                displayFormat='MM/dd/yyyy'
                                iconPosition={
                                  INPUT_DATE_PICKER_ICON_POSITION.END
                                }
                                allowClear={false}
                                className='h-9 px-2'
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {preview && preview.pagination.totalPages > 1 && (
                  <div className='mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4'>
                    <p className='text-sm text-muted-foreground'>
                      Showing{' '}
                      {(preview.pagination.page - 1) *
                        preview.pagination.limit +
                        1}
                      -
                      {Math.min(
                        preview.pagination.page * preview.pagination.limit,
                        preview.pagination.total,
                      )}{' '}
                      of {preview.pagination.total} classes ·{' '}
                      {selectedClassIds.size} selected
                    </p>
                    <div className='flex items-center gap-2'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        disabled={
                          previewLoading || preview.pagination.page === 1
                        }
                        onClick={() => {
                          setClassPage((page) => Math.max(1, page - 1));
                        }}
                      >
                        Previous
                      </Button>
                      <span className='min-w-24 text-center text-sm'>
                        Page {preview.pagination.page} of{' '}
                        {preview.pagination.totalPages}
                      </span>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        disabled={
                          previewLoading ||
                          preview.pagination.page ===
                            preview.pagination.totalPages
                        }
                        onClick={() => {
                          setClassPage((page) =>
                            Math.min(preview.pagination.totalPages, page + 1),
                          );
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {rolloverMode === 'copy' && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>
                    What should happen to the old enrollments?
                  </CardTitle>
                  <p className='text-sm text-muted-foreground'>
                    This only affects the classes in{' '}
                    {sourceSession?.name ?? 'the session that is ending'}.
                  </p>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={disposition}
                    onValueChange={(value) => {
                      const next =
                        value as RolloverEnrollmentDisposition;
                      setDisposition(next);
                      if (next === 'keep') {
                        setCurrentStep((step) => Math.min(step, 1));
                      }
                    }}
                    className='grid gap-3'
                  >
                    <Label className='flex items-start gap-2'>
                      <RadioGroupItem value='keep' className='mt-0.5' />
                      <span>
                        Only copy empty classes
                        <span className='block text-xs text-muted-foreground'>
                          Creates class shells in the new session with updated
                          dates. Students stay in the old session. Nothing is
                          closed or deleted, and no fees are added.
                        </span>
                      </span>
                    </Label>
                    <Label className='flex items-start gap-2'>
                      <RadioGroupItem value='history' className='mt-0.5' />
                      <span>
                        Move students, then end old enrollments in history
                        (recommended for a full rollover)
                        <span className='block text-xs text-muted-foreground'>
                          Students are enrolled in the new classes. The old
                          enrollments are marked as finished and stay on the
                          student record and in your reports. If this rollover
                          was a mistake, undoing it puts them back.
                        </span>
                      </span>
                    </Label>
                    <Label className='flex items-start gap-2 text-destructive'>
                      <RadioGroupItem value='delete' className='mt-0.5' />
                      <span>
                        Move students, then delete old enrollments permanently
                        <span className='block text-xs'>
                          Students are enrolled in the new classes. The old
                          enrollments are erased and cannot be brought back,
                          even if you undo this rollover afterwards.
                        </span>
                      </span>
                    </Label>
                  </RadioGroup>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {currentStep === 2 && !classesOnlyCopy && (
          <div className='grid gap-6'>
            <Alert>
              <AlertCircle className='size-4' />
              <AlertTitle>No card will be charged today</AlertTitle>
              <AlertDescription>
                Any fee you select will be added to the family account as an
                unpaid invoice. No fees are selected by default.
              </AlertDescription>
            </Alert>
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>
                  Do you want to bill any fees for continuing students?
                </CardTitle>
              </CardHeader>
              <CardContent className='grid gap-5'>
                {(
                  [
                    [
                      'sessionRegistration',
                      'Session registration fee',
                      'Bills your usual registration fee again for the new session, using your existing family and per-student amounts.',
                    ],
                    [
                      'classRegistration',
                      'Class registration fee',
                      'Bills the per-class registration fee for every continuing student.',
                    ],
                    [
                      'costume',
                      'Costume fee',
                      'Bills a costume fee for every continuing student. You can override the amount below without changing the class list price yet.',
                    ],
                  ] as const
                ).map(([key, title, description]) => (
                  <div
                    key={key}
                    className='flex items-center justify-between gap-6 rounded-lg border p-4'
                  >
                    <div>
                      <Label htmlFor={key}>{title}</Label>
                      <p className='text-xs text-muted-foreground'>
                        {description}
                      </p>
                    </div>
                    <Switch
                      id={key}
                      checked={Boolean(fees[key])}
                      onCheckedChange={(checked) => {
                        setFees((current) => ({
                          ...current,
                          [key]: checked,
                        }));
                      }}
                    />
                  </div>
                ))}
                {fees.costume && (
                  <div className='grid gap-2 rounded-lg border p-4'>
                    <Label htmlFor='costume-amount'>
                      Costume amount for continuing students (optional)
                    </Label>
                    <Input
                      id='costume-amount'
                      type='number'
                      min='0'
                      step='0.01'
                      placeholder='Uses each class costume fee when blank'
                      value={costumeAmountInput}
                      onChange={(event) => {
                        setCostumeAmountInput(event.target.value);
                      }}
                    />
                  </div>
                )}
                {(fees.sessionRegistration || fees.classRegistration) && (
                  <div className='grid gap-4 rounded-lg border p-4'>
                    <p className='text-sm font-medium'>
                      Charge these fees to continuing students?
                    </p>
                    {fees.sessionRegistration && (
                      <div className='flex items-center justify-between gap-6'>
                        <Label htmlFor='charge-session-continuing'>
                          Session registration for continuers
                        </Label>
                        <Switch
                          id='charge-session-continuing'
                          checked={
                            fees.chargeSessionRegistrationForContinuing !==
                            false
                          }
                          onCheckedChange={(checked) => {
                            setFees((current) => ({
                              ...current,
                              chargeSessionRegistrationForContinuing: checked,
                            }));
                          }}
                        />
                      </div>
                    )}
                    {fees.classRegistration && (
                      <div className='flex items-center justify-between gap-6'>
                        <Label htmlFor='charge-class-continuing'>
                          Class registration for continuers
                        </Label>
                        <Switch
                          id='charge-class-continuing'
                          checked={
                            fees.chargeClassRegistrationForContinuing !== false
                          }
                          onCheckedChange={(checked) => {
                            setFees((current) => ({
                              ...current,
                              chargeClassRegistrationForContinuing: checked,
                            }));
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>
                  Future list prices for new portal enrollments (optional)
                </CardTitle>
                <p className='text-sm text-muted-foreground'>
                  Applied after continuer invoices so new families see these
                  amounts. Leave blank to keep current class and session fees.
                </p>
              </CardHeader>
              <CardContent className='grid gap-4 sm:grid-cols-3'>
                <div className='grid gap-2'>
                  <Label htmlFor='future-costume'>Costume fee</Label>
                  <Input
                    id='future-costume'
                    type='number'
                    min='0'
                    step='0.01'
                    value={futureCostumeFeeInput}
                    onChange={(event) => {
                      setFutureCostumeFeeInput(event.target.value);
                    }}
                  />
                </div>
                <div className='grid gap-2'>
                  <Label htmlFor='future-class-reg'>Class registration</Label>
                  <Input
                    id='future-class-reg'
                    type='number'
                    min='0'
                    step='0.01'
                    value={futureClassRegFeeInput}
                    onChange={(event) => {
                      setFutureClassRegFeeInput(event.target.value);
                    }}
                  />
                </div>
                <div className='grid gap-2'>
                  <Label htmlFor='future-session-fee'>
                    Session fee (1st student)
                  </Label>
                  <Input
                    id='future-session-fee'
                    type='number'
                    min='0'
                    step='0.01'
                    value={futureSessionFeeInput}
                    onChange={(event) => {
                      setFutureSessionFeeInput(event.target.value);
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </Stepper>
    </div>
  );
}

function SessionRolloverProgress({
  job,
  jobId,
  onClose,
}: {
  job?: SessionRolloverJob;
  jobId: string;
  onClose: () => void;
}) {
  const retryMutation = useRetrySessionRollover();
  const cancelMutation = useCancelSessionRollover();
  const deleteMutation = useDeleteSessionRollover();
  const isRunning = job?.status === 'queued' || job?.status === 'processing';

  const phaseLabel = job
    ? job.phase.split('_').join(' ')
    : 'Loading your rollover';

  const deleteDestination = async () => {
    if (
      !window.confirm(
        'This removes the classes, enrollments, and invoices this rollover created in the new session, and puts the old enrollments back. Continue?',
      )
    ) {
      return;
    }
    const result = await deleteMutation.mutateAsync(jobId);
    result.warnings.forEach((warning) => toast.warning(warning));
    toast.success('Everything this rollover created has been removed.');
    sessionStorage.removeItem(ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY);
    onClose();
  };

  return (
    <div className='mx-auto grid w-full max-w-4xl gap-6'>
      <div className='flex items-center gap-2'>
        <Button
          variant='ghost'
          className='flex items-center p-2 [&_svg]:size-6'
          title='Back to sessions'
          onClick={onClose}
        >
          <CircleArrowLeft className='size-6' />
        </Button>
        <div>
          <h1 className='text-2xl font-semibold'>Rollover progress</h1>
          <p className='capitalize text-muted-foreground'>{phaseLabel}</p>
        </div>
      </div>
      <Card>
        <CardContent className='grid gap-4 pt-6'>
          <div className='flex items-center justify-between text-sm'>
            <span>{job?.currentItem ?? 'Getting started...'}</span>
            <span>{job?.progress ?? 0}% done</span>
          </div>
          <Progress value={job?.progress ?? 0} />
          {job?.errorMessage && (
            <Alert variant='destructive'>
              <AlertCircle className='size-4' />
              <AlertDescription>{job.errorMessage}</AlertDescription>
            </Alert>
          )}
          <ScrollArea className='h-64 rounded-md border p-3'>
            <div className='grid gap-2'>
              {[...(job?.events ?? [])].reverse().map((event, index) => (
                <div
                  key={`${event.at}-${index}`}
                  className='border-b pb-2 text-sm last:border-0'
                >
                  <span className='text-xs text-muted-foreground'>
                    {new Date(event.at).toLocaleTimeString()}
                  </span>
                  <p
                    className={
                      event.level === 'error'
                        ? 'text-destructive'
                        : event.level === 'warn'
                          ? 'text-amber-700'
                          : undefined
                    }
                  >
                    {event.message}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className='flex flex-wrap justify-end gap-2'>
            <Button variant='outline' onClick={onClose}>
              Back to sessions
            </Button>
            {isRunning && (
              <Button
                variant='outline'
                disabled={cancelMutation.isPending}
                onClick={() => void cancelMutation.mutateAsync(jobId)}
              >
                Stop rollover
              </Button>
            )}
            {job?.canRetry && (
              <Button
                disabled={retryMutation.isPending}
                onClick={() => {
                  sessionStorage.setItem(
                    ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY,
                    jobId,
                  );
                  void retryMutation.mutateAsync(jobId);
                }}
              >
                <RefreshCw />
                Try again
              </Button>
            )}
            {job?.canDelete && (
              <Button
                variant='destructive'
                disabled={deleteMutation.isPending}
                onClick={() => void deleteDestination()}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className='animate-spin' />
                ) : (
                  <Trash2 />
                )}
                {deleteMutation.isPending
                  ? 'Undoing rollover...'
                  : 'Undo this rollover'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
